'use client'

import { Icon } from '@/components/ui/icon'
import { TimeDisplay } from '@/components/ui/time-display'
import { usePendingAppointments } from '@/hooks/use-pending-appointments'
import { useQuoteCalculator } from '@/hooks/use-quote-calculator'
import { useAppDispatch, useAppSelector } from '@/lib/store'
import { clearBookingState, removeSelectedService, setActiveStep, setDrawerOpen } from '@/lib/store/features/booking/bookingSlice'
import { BookingStep } from '@/lib/types/operations/booking'
import { cn } from '@/lib/utils'
import { formatPrice } from '@/lib/utils/price'
import { Button } from '@/components/ui/button'
import { useSession } from 'next-auth/react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useMemo } from 'react'
import { BOOKING_STEPS } from '../../constants'

function formatServiceId(serviceId: string): string {
  return serviceId
    .replace(/^srv_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function ReservationDrawerPanel({ onClose }: { onClose: () => void }) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const dispatch = useAppDispatch()
  const selectedServiceIds = useAppSelector((state) => state.booking.persisted.selectedServiceIds)
  const selectedOption = useAppSelector((state) => state.booking.persisted.selectedOption)
  const vehicleDetails = useAppSelector((state) => state.booking.persisted.vehicleDetails)
  const shopLocationId = useAppSelector((state) => state.booking.persisted.shopLocationId)
  const contactInfo = useAppSelector((state) => state.booking.persisted.contactInfo)
  const activeStep = useAppSelector((state) => state.booking.current.activeStep)
  const { status: authStatus } = useSession()
  const locations = useAppSelector((state) => state.locations.items)
  const selectedLocation = locations.find(loc => loc.id === shopLocationId)
  const services = useAppSelector((state) => state.services.items)
  const { hasPendingAppointments, oldestPendingAppointment, goToPayment, resetPendingAppointment, isLoading: isPendingLoading } = usePendingAppointments()

  // Pass the vehicle itself, not a definition id: the estimator resolves make/model/
  // year (the same path the wizard's own pricing uses), which is what lets the vehicle
  // risk factor apply to a luxury or complex car.
  const drawerVehicleYear = Number(vehicleDetails?.year)
  const { calculation: quoteEstimate, isCalculating: isEstimating } = useQuoteCalculator({
    shopLocationId,
    make: vehicleDetails?.make ?? undefined,
    model: vehicleDetails?.model ?? undefined,
    year: Number.isFinite(drawerVehicleYear) ? drawerVehicleYear : undefined,
    trim: vehicleDetails?.trim ?? undefined,
    serviceIds: selectedServiceIds,
    enabled: selectedServiceIds.length > 0 && !!shopLocationId,
  })

  const getServiceName = useCallback((serviceId: string) => {
    const service = services.find(s => s.id === serviceId)
    return service?.name || formatServiceId(serviceId)
  }, [services])

  const getNextRequiredStep = useCallback(() => {
    if (!vehicleDetails?.make || !vehicleDetails?.model) {
      return { nextStep: 'vehicle' as BookingStep, ctaText: 'Continue with vehicle details' }
    }
    if (selectedServiceIds.length === 0) {
      return { nextStep: 'services' as BookingStep, ctaText: 'Select your services' }
    }
    if (!selectedOption) {
      return { nextStep: 'schedule' as BookingStep, ctaText: 'Schedule your drop-off' }
    }
    return { nextStep: 'review' as BookingStep, ctaText: 'Review and submit booking' }
  }, [vehicleDetails, selectedServiceIds, selectedOption])

  const { nextStep, ctaText } = useMemo(() => getNextRequiredStep(), [getNextRequiredStep])

  const handleContinue = useCallback(() => {
    dispatch(setActiveStep(nextStep))
    const newParams = new URLSearchParams(searchParams.toString())
    newParams.set('step', nextStep)
    router.push(`/book?${newParams.toString()}`)
    dispatch(setDrawerOpen(false))
  }, [dispatch, router, nextStep, searchParams])

  const handleClear = useCallback(() => {
    dispatch(clearBookingState())
  }, [dispatch])

  const handleCancelPendingAppointment = useCallback(async () => {
    await resetPendingAppointment()
    dispatch(clearBookingState())
    dispatch(setDrawerOpen(false))
  }, [resetPendingAppointment, dispatch])

  // Close the drawer before navigating to the payment page so it doesn't stay
  // open on top of /pay.
  const handleGoToPayment = useCallback(() => {
    dispatch(setDrawerOpen(false))
    goToPayment()
  }, [dispatch, goToPayment])

  const handleStepClick = useCallback((stepId: string) => {
    const step = BOOKING_STEPS.find(s => s.id === stepId)
    if (step) {
      const newParams = new URLSearchParams(searchParams.toString())
      newParams.set('step', step.id)
      router.push(`/book?${newParams.toString()}`)
      dispatch(setDrawerOpen(false))
    }
  }, [dispatch, router, searchParams])

  const getStepStatus = useCallback((stepId: string) => {
    switch (stepId) {
      case 'vehicle':
        return vehicleDetails?.make && vehicleDetails?.model ? 'completed' : activeStep === stepId ? 'current' : 'pending'
      case 'services':
        return selectedServiceIds.length > 0 ? 'completed' : activeStep === stepId ? 'current' : 'pending'
      case 'schedule':
        return selectedOption ? 'completed' : activeStep === stepId ? 'current' : 'pending'
      case 'review':
        return activeStep === stepId ? 'current' : 'pending'
      default:
        return 'pending'
    }
  }, [activeStep, shopLocationId, vehicleDetails, selectedServiceIds, authStatus, selectedOption])

  const hasValidBookingState = (Array.isArray(selectedServiceIds) && selectedServiceIds.length > 0) || hasPendingAppointments

  if (!hasValidBookingState) return null

  return (
    <div className="space-y-4">
      {hasPendingAppointments && oldestPendingAppointment ? (
        <PendingAppointmentView
          appointment={oldestPendingAppointment}
          goToPayment={handleGoToPayment}
          onCancel={handleCancelPendingAppointment}
          isLoading={isPendingLoading}
        />
      ) : (
        <>
          <div className="space-y-1.5">
            {BOOKING_STEPS.map((step, index) => {
              const status = getStepStatus(step.id)
              const isCompleted = status === 'completed'
              const isCurrent = status === 'current'

              return (
                <div
                  key={step.id}
                  role="button"
                  tabIndex={0}
                  className={cn(
                    'w-full rounded-lg p-3 text-left transition-colors cursor-pointer',
                    isCompleted && 'bg-accent/5 hover:bg-accent/10',
                    isCurrent && 'bg-accent/10',
                    !isCompleted && !isCurrent && 'bg-overlay/30 hover:bg-overlay/50'
                  )}
                  onClick={() => handleStepClick(step.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleStepClick(step.id) }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'flex justify-center items-center w-6 h-6 rounded-full text-xs shrink-0',
                        isCompleted && 'bg-accent text-accent-foreground',
                        isCurrent && 'bg-accent/20 text-foreground',
                        !isCompleted && !isCurrent && 'bg-overlay text-muted'
                      )}
                    >
                      {isCompleted ? (
                        <Icon icon="check" className="w-3.5 h-3.5" />
                      ) : (
                        <span className="font-medium">{index + 1}</span>
                      )}
                    </div>

                    <span className={cn(
                      'font-medium text-sm',
                      isCompleted && 'text-foreground',
                      isCurrent && 'text-foreground',
                      !isCompleted && !isCurrent && 'text-muted'
                    )}>
                      {step.title}
                    </span>
                  </div>

                  {isCompleted && (
                    <div className="mt-2 ml-9">
                      <StepCompletedDetails
                        stepId={step.id}
                        shopLocationId={shopLocationId}
                        selectedLocation={selectedLocation}
                        vehicleDetails={vehicleDetails}
                        selectedServiceIds={selectedServiceIds}
                        selectedOption={selectedOption}
                        contactInfo={contactInfo}
                        getServiceName={getServiceName}
                        onRemoveService={(serviceId) => {
                          dispatch(removeSelectedService(serviceId))
                        }}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Running Estimate */}
          {quoteEstimate && (
            <div className="rounded-lg bg-accent/5 border border-accent/20 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted">Estimated Total</span>
                <span className="text-lg font-bold text-foreground">
                  {isEstimating ? '...' : formatPrice(quoteEstimate.total)}
                </span>
              </div>
              <p className="text-xs text-muted mt-1">Final price confirmed at review</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-border">
            <button
              type="button"
              onClick={handleClear}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-muted rounded-lg hover:bg-overlay transition-colors"
            >
              <Icon icon="x" className="w-3.5 h-3.5" /> Clear
            </button>
            <Button
              size="sm"
              onPress={handleContinue}
              variant="primary"
            >
              {ctaText} <Icon icon="arrow-right" className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

function PendingAppointmentView({
  appointment,
  goToPayment,
  onCancel,
  isLoading
}: {
  appointment: { timeSlots?: Array<{ startTimeISO?: string; endTimeISO?: string }>; services?: Array<{ name?: string }>; shopLocation?: { name?: string; address?: string } }
  goToPayment: () => void
  onCancel: () => void
  isLoading: boolean
}) {
  return (
    <div className="space-y-4">
      {/* Task 15F: was arbitrary v1 hex '#e2a727' for a warning banner;
          bg-warning-soft/border-warning is the same tinted-warning pattern
          used elsewhere (attention-required.tsx, OnCallAgenda.tsx). The
          icon is solid --warning against its real container, the
          warning-soft tint (not plain white): 4.49:1, over the 3:1
          non-text floor for a decorative icon glyph. */}
      <div className="p-4 rounded-lg bg-warning-soft border border-warning/30">
        <div className="flex items-start gap-3">
          <Icon icon="warning" className="w-5 h-5 text-warning shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h3 className="font-medium text-foreground">You have a pending appointment</h3>
            <p className="text-sm text-muted">
              Complete payment or cancel to create a new booking.
            </p>
          </div>
        </div>
      </div>

      {appointment.timeSlots && appointment.timeSlots.length > 0 && (
        <div className="p-3 rounded-lg bg-overlay/50">
          <div className="flex items-center gap-2 mb-2">
            <Icon icon="clock" className="w-4 h-4 text-accent" />
            <span className="font-medium text-sm text-foreground">Appointment Time</span>
          </div>
          <div className="text-sm text-muted">
            {(() => {
              const firstSlot = appointment.timeSlots![0]
              const lastSlot = appointment.timeSlots![appointment.timeSlots!.length - 1]
              if (firstSlot?.startTimeISO && lastSlot?.endTimeISO) {
                return (
                  <>
                    <TimeDisplay utcDateTime={firstSlot.startTimeISO} format="datetime" /> - <TimeDisplay utcDateTime={lastSlot.endTimeISO} format="time" />
                  </>
                )
              }
              return 'Time details unavailable'
            })()}
          </div>
        </div>
      )}

      {appointment.services && appointment.services.length > 0 && (
        <div className="p-3 rounded-lg bg-overlay/50">
          <div className="flex items-center gap-2 mb-2">
            <Icon icon="content-product" className="w-4 h-4 text-accent" />
            <span className="font-medium text-sm text-foreground">
              Service{appointment.services.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="text-sm text-muted">
            {appointment.services.map(s => s.name).filter(Boolean).join(', ')}
          </div>
        </div>
      )}

      {appointment.shopLocation && (
        <div className="p-3 rounded-lg bg-overlay/50">
          <div className="flex items-center gap-2 mb-2">
            <Icon icon="map" className="w-4 h-4 text-accent" />
            <span className="font-medium text-sm text-foreground">Location</span>
          </div>
          <div className="text-sm text-muted">
            {appointment.shopLocation.name || 'Selected location'}
            {appointment.shopLocation.address && <div>{appointment.shopLocation.address}</div>}
          </div>
        </div>
      )}

      <div className="space-y-3 pt-4 border-t border-border">
        <Button
          variant="primary"
          className="w-full"
          onPress={goToPayment}
          isLoading={isLoading}
        >
          <Icon icon="credit-card" className="w-4 h-4" /> Complete Payment
        </Button>
        <Button
          variant="secondary"
          className="w-full"
          onPress={onCancel}
          isLoading={isLoading}
        >
          <Icon icon="x" className="w-4 h-4" /> Cancel Appointment
        </Button>
      </div>
    </div>
  )
}

function StepCompletedDetails({
  stepId,
  shopLocationId,
  selectedLocation,
  vehicleDetails,
  selectedServiceIds,
  selectedOption,
  contactInfo,
  getServiceName,
  onRemoveService
}: {
  stepId: string
  shopLocationId: string | null
  selectedLocation?: { name?: string; address?: string; city?: string }
  vehicleDetails: { year?: string | null; make?: string | null; model?: string | null } | null
  selectedServiceIds: string[]
  selectedOption: { optionId: string; wallClockStart: string; wallClockEnd: string; nightsOvernight: number; installerCount: number; appointmentId?: string; expiresAt?: string } | null
  contactInfo: { email: string | null; phone: string | null } | null
  getServiceName: (id: string) => string
  onRemoveService: (id: string) => void
}) {
  switch (stepId) {
    case 'vehicle':
      if (!vehicleDetails?.make) return null
      return (
        <div className="text-sm text-muted">
          {[vehicleDetails.year, vehicleDetails.make, vehicleDetails.model].filter(Boolean).join(' ')}
        </div>
      )

    case 'services':
      if (selectedServiceIds.length === 0) return null
      return (
        <div className="flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
          {selectedServiceIds.map((serviceId) => (
            <button
              key={serviceId}
              type="button"
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-overlay text-muted hover:bg-danger/10 hover:text-foreground transition-colors"
              onClick={() => onRemoveService(serviceId)}
            >
              {getServiceName(serviceId)}
              <Icon icon="x" className="w-3 h-3" />
              <span className="sr-only">Remove service</span>
            </button>
          ))}
        </div>
      )

    case 'schedule':
      if (!selectedOption) return null
      return (
        <div className="text-sm text-muted">
          <div className="flex items-center gap-2">
            <Icon icon="clock" className="w-3.5 h-3.5" />
            <span>
              <TimeDisplay utcDateTime={selectedOption.wallClockStart} format="datetime" /> -{' '}
              <TimeDisplay utcDateTime={selectedOption.wallClockEnd} format="time" />
            </span>
          </div>
        </div>
      )

    default:
      return null
  }
}
